/**
 * localStorage persistence for workflow builder drafts.
 *
 * Drafts are keyed by draft id (`DefinitionState.id`). A top-level index
 * stores the ordered list of known draft ids so the drafts page can
 * enumerate them efficiently.
 */

import type { DefinitionState } from '~/pages/workflows/builder/types';

const INDEX_KEY = 'cara:workflows:builder:drafts:index';
const DRAFT_PREFIX = 'cara:workflows:builder:draft:';

export interface DraftSummary {
  id: string;
  label: string;
  updatedAt: number;
  nodeCount: number;
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function listDrafts(): DraftSummary[] {
  const index = safeParse<string[]>(localStorage.getItem(INDEX_KEY)) ?? [];
  const summaries: DraftSummary[] = [];
  for (const id of index) {
    const state = safeParse<DefinitionState>(localStorage.getItem(DRAFT_PREFIX + id));
    if (!state) continue;
    summaries.push({
      id: state.id,
      label: state.label,
      updatedAt: state.updatedAt,
      nodeCount: state.nodes.length
    });
  }
  summaries.sort((a, b) => b.updatedAt - a.updatedAt);
  return summaries;
}

export function loadDraft(id: string): DefinitionState | null {
  return safeParse<DefinitionState>(localStorage.getItem(DRAFT_PREFIX + id));
}

export function saveDraft(state: DefinitionState): void {
  localStorage.setItem(DRAFT_PREFIX + state.id, JSON.stringify(state));
  const index = safeParse<string[]>(localStorage.getItem(INDEX_KEY)) ?? [];
  if (!index.includes(state.id)) {
    index.unshift(state.id);
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  }
}

export function deleteDraft(id: string): void {
  localStorage.removeItem(DRAFT_PREFIX + id);
  const index = safeParse<string[]>(localStorage.getItem(INDEX_KEY)) ?? [];
  localStorage.setItem(INDEX_KEY, JSON.stringify(index.filter((k) => k !== id)));
}
